import { useRouter } from 'next/router';
import useLocalStorage from '@rehooks/local-storage';
import { useEffect, useState } from 'react';

import type { LeftNavUserTeam } from '~/components/LeftNav/LeftNavPure';
import { useDeferAfterPaint } from '@/hooks/useDeferAfterPaint';
import { isMacLike, useKey } from '@poseidon/components/hooks/useKey';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import { LeftNavPure } from '~/components/LeftNav/LeftNavPure';
import { aggregateSendsToday, todayMeterState } from '~/components/SendsTodayStrip.utils';

// Plays the nav-badge shake for ~1s whenever `value` changes to a non-zero
// count. Lifted out of `NotificationLabel` so that component renders purely
// from props.
function useShakeOnChange(value: number | undefined): boolean {
  const [isShaking, setIsShaking] = useState(false);
  useEffect(() => {
    if (value == null || value === 0) return;
    setIsShaking(true);
    const timeout = setTimeout(() => setIsShaking(false), 1000);
    return () => clearTimeout(timeout);
  }, [value]);
  return isShaking;
}

/**
 * Stateful container for the left nav. Owns everything impure — router,
 * localStorage, auth context, the badge-count tRPC queries, the global keyboard
 * shortcuts, the per-badge shake timers, and the sublink open/close state — then
 * hands the resolved data to the pure `LeftNavPure` (and, through it, a fully
 * pure subtree) for rendering.
 */
export const LeftNav = (props: { userTeam: LeftNavUserTeam }) => {
  const router = useRouter();
  const [navMinimized, setNavMinimized] = useLocalStorage('navMinimized', false);
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

  // This only feeds a nav badge and is one of the slowest calls on the page, so
  // defer it past first paint to keep it off the critical load path.
  const deferred = useDeferAfterPaint();
  const hubspotCountsQuery = api.hubspot.getMatchesCount.useQuery(
    { teamId: props.userTeam.teamId },
    {
      enabled: deferred && props.userTeam.hubspotConnected != null && props.userTeam.hubspotConnected != 'off',
      // This is a relatively expensive count that barely changes between page
      // loads; keep it fresh for 5 min so navigation doesn't re-run it. HubSpot
      // mutations still invalidate it explicitly (useHubspotMutation).
      staleTime: 5 * 60 * 1000,
    },
  );
  const hubspotProblemCount = hubspotCountsQuery.data
    ? (hubspotCountsQuery.data.unsure_match ?? 0) +
      (hubspotCountsQuery.data.error ?? 0) +
      (hubspotCountsQuery.data.incomplete_contact ?? 0)
    : undefined;

  // Query AI Step count for Take Action badge
  const aiStepCountQuery = api.aiStep.countUnfinished.useQuery(
    {
      teamId: props.userTeam.teamId,
      userId: props.userTeam.userId,
    },
    { enabled: true, refetchInterval: 60000 }, // Refetch every minute
  );

  const takeActionCount = aiStepCountQuery.data?.count ?? 0;

  // Only while impersonating a teammate do we surface team-wide replies. Gated
  // here so regular sessions never see the badge (and never run the query).
  const [auth] = useAuthContext();
  const isImpersonating = !!auth.impersonation;
  const repliesQuery = api.contact.unhandledRepliesByPersona.useQuery(
    { teamId: props.userTeam.teamId },
    { enabled: isImpersonating, refetchInterval: 60000, staleTime: 60 * 1000 },
  );
  const repliesCount = repliesQuery.data?.total ?? 0;

  // Sidebar "Today" meter: this person's sends against their daily caps. Deferred past first
  // paint like the other badges, and refreshed every few minutes (the counts move slowly).
  const sendsTodayQuery = api.user.sendsToday.useQuery(
    { teamId: props.userTeam.teamId },
    { enabled: deferred, refetchInterval: 5 * 60 * 1000, staleTime: 60 * 1000 },
  );
  const todaySends = (() => {
    const data = sendsTodayQuery.data;
    if (data == null) return undefined;
    const entries = aggregateSendsToday([{ userId: props.userTeam.userId, throttling: data.throttling }]);
    return {
      entries,
      state: todayMeterState(entries, {
        hasActiveEnrollments: data.hasActiveEnrollments,
        timezoneName: data.timezoneName,
        now: new Date(),
      }),
    };
  })();

  const takeActionShaking = useShakeOnChange(takeActionCount);
  const repliesShaking = useShakeOnChange(repliesCount);
  const hubspotShaking = useShakeOnChange(hubspotProblemCount);

  useKey('b', () => setNavMinimized(!navMinimized), { metaWindowsCtrlKey: true });
  useKey(
    'k',
    (e) => {
      // prevent default browser ctrl+k shortcut
      e.preventDefault();

      const page = '/prospects';
      if (router.pathname === page) {
        document.getElementById('search_input')?.focus();
      } else {
        void router.replace(page);
      }
    },
    // exactShift: ⌘⇧K is the superadmin switch palette, so ⌘K must not also fire on it.
    { metaWindowsCtrlKey: true, exactShift: false },
  );

  return (
    <LeftNavPure
      userTeam={props.userTeam}
      navMinimized={navMinimized}
      setNavMinimized={setNavMinimized}
      pathname={router.pathname}
      isMac={isMacLike()}
      isImpersonating={isImpersonating}
      takeActionCount={takeActionCount}
      repliesCount={repliesCount}
      hubspotProblemCount={hubspotProblemCount}
      takeActionShaking={takeActionShaking}
      repliesShaking={repliesShaking}
      hubspotShaking={hubspotShaking}
      openRows={openRows}
      onRowOpenChange={(key, open) => setOpenRows((prev) => ({ ...prev, [key]: open }))}
      todaySends={todaySends}
    />
  );
};
